import React, { useState } from 'react';

import { FormComponent, UiComponent } from 'components';
import { AppRouteQuery, FileServiceQuery, QuickMenuQuery } from 'queries';
import useModal from 'hooks/useModal';
import useFileService from 'hooks/useFileService';
import { toast } from 'react-toastify';
import { DEFAULT_ERROR_MESSAGE } from 'data/message';
import { IQuickMenu, QuickMenuExposureType } from 'types';
import { QUICK_MENU_UPLOAD_DIRECTORY } from '../constants';
import UserGroupSelect from './UserGroupSelect';
import * as FormStyle from './style';

interface Props {
  quickMenu?: IQuickMenu;
}

type FormValueType = { title: string; routeId: string };

function Form({ quickMenu }: Props) {
  const { handleClose, handleAlert } = useModal();
  const { mutateAsync: create } = QuickMenuQuery.usePost();
  const { mutateAsync: update } = QuickMenuQuery.usePut();
  const { handleUploadd: uploadIcon } = FileServiceQuery.useUpload(
    QUICK_MENU_UPLOAD_DIRECTORY,
  );
  const { data: routes = [] } = AppRouteQuery.useGetAll();
  const [isSaving, setIsSaving] = useState(false);
  const [exposureType, setExposureType] = useState<QuickMenuExposureType>(
    quickMenu?.exposureType ?? 'NONE',
  );
  const [userGroups, setUserGroups] = useState(quickMenu?.userGroups ?? []);

  const icon = useFileService({ initFileUrl: quickMenu?.iconUrl });

  const submit = async (data: FormValueType) => {
    if (isSaving) return;
    const title = data.title?.trim();
    if (!icon.fileUrl) {
      handleAlert('아이콘 이미지를 등록해주세요');
      return;
    }
    if (!title) {
      handleAlert('타이틀을 입력해주세요');
      return;
    }
    const routeId = Number(data.routeId);
    if (!routeId) {
      handleAlert('경로를 선택해주세요');
      return;
    }
    if (exposureType === 'USER_GROUP' && userGroups.length === 0) {
      handleAlert('노출할 회원 그룹을 선택해주세요');
      return;
    }
    const exposure = {
      exposureType,
      userGroupIds:
        exposureType === 'USER_GROUP' ? userGroups.map(({ id }) => id) : [],
    };

    setIsSaving(true);
    try {
      let iconUrl = quickMenu?.iconUrl ?? '';
      if (icon.file) {
        const res = await uploadIcon(icon.file);
        if (!res) return;
        iconUrl = res.data.url;
      }

      if (quickMenu) {
        await update({
          id: quickMenu.id,
          title,
          iconUrl,
          routeId,
          ...exposure,
        });
        toast.success('퀵메뉴가 수정되었습니다.');
      } else {
        await create({ title, iconUrl, routeId, ...exposure });
        toast.success('퀵메뉴가 추가되었습니다.');
      }
      handleClose();
    } catch {
      toast.error(DEFAULT_ERROR_MESSAGE);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <FormComponent.Form css={FormStyle.wrap} submit={submit}>
      <div css={FormStyle.field}>
        <UiComponent.FilterTitle title="아이콘 이미지" required />
        <UiComponent.FileUpload
          name="icon"
          fileUrl={icon.fileUrl}
          handleFile={icon.handleFile}
        />
        <p css={FormStyle.hint}>PNG / JPG, 1MB 이하</p>
      </div>

      <div css={FormStyle.field}>
        <UiComponent.FilterTitle title="타이틀" required />
        <FormComponent.Input
          name="title"
          defaultValue={quickMenu?.title}
          maxLength={20}
          placeholder="최대 20자"
        />
      </div>

      <div css={FormStyle.field}>
        <UiComponent.FilterTitle title="경로" required />
        <FormComponent.Select
          // 선택지가 늦게 도착해도 기존 값이 잡히도록 로드 후 다시 마운트
          key={routes.length}
          name="routeId"
          options={routes.map((r) => ({ name: r.name, value: String(r.id) }))}
          defaultValue={quickMenu ? String(quickMenu.routeId) : ''}
          defaultTitle="경로 선택"
        />
      </div>

      <div css={FormStyle.field}>
        <UiComponent.FilterTitle title="노출 대상" required />
        <div css={FormStyle.radios}>
          <FormComponent.Radio
            name="exposureType"
            value="NONE"
            current={exposureType}
            handleClick={setExposureType}
          >
            전체
          </FormComponent.Radio>
          <FormComponent.Radio
            name="exposureType"
            value="USER_GROUP"
            current={exposureType}
            handleClick={setExposureType}
          >
            회원 그룹
          </FormComponent.Radio>
        </div>
        {exposureType === 'USER_GROUP' && (
          <UserGroupSelect selected={userGroups} onChange={setUserGroups} />
        )}
      </div>

      <div css={FormStyle.footer}>
        <UiComponent.Button
          variant="border"
          color="primary"
          onClick={handleClose}
        >
          취소
        </UiComponent.Button>
        <UiComponent.Button
          type="submit"
          variant="contained"
          color="primary"
          disabled={isSaving}
        >
          저장하기
        </UiComponent.Button>
      </div>
    </FormComponent.Form>
  );
}

export default Form;
